import { Component, DestroyRef, effect, inject, OnInit, Signal, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ListLayoutComponent,
	TitleGroupComponent,
	ToolbarComponent,
	TableComponent,
	IToolbar,
	ITableHeader,
	ITableConfig,
	ITableRow,
	ITableCellEmitter,
	AlertComponent,
	ButtonComponent,
	ITableSortEvent,
	TableFilterComponent,
	SelectComponent,
	ISelect,
} from '@tecalisdev/infinity';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ProcessService } from '../../services/process.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { MasterService } from '../../../shared/services/master.service';
import { ActivatedRoute } from '@angular/router';
import { formatFilterTable, transformTrimObject } from '../../../core/utils/utils';
import { Observable } from 'rxjs';
import { ToolbarHelperService } from '../../../shared/services/toolbar-helper.service';
import {
	IProcessListBody,
	IProcessListPayload,
	IProcessListResponse,
	PROCESS_STATUS,
} from '../../models/processes.models';
import { HttpErrorResponse } from '@angular/common/http';
import { PaginatedListController } from '../../../shared/list/paginated-list.controller';
import { MenuItemAction } from '../../../shared/models/master.models';
import { Location } from '@angular/common';
import { ModalCreateProcessComponent } from '../../components/modal-create-process/modal-create-process.component';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { Permission } from '../../../core/models/role-permissions.models';
import { environment } from '../../../../environments/environment';
import { buildCurlPostJson } from '../../../panel/utils/build-curl.util';
import { ModalCurlRequestComponent } from '../../../shared/components/modal-curl-request/modal-curl-request.component';
import { ModalConfirmationService } from '../../../shared/services/modal-confirmation.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { NavigationService } from '../../../shared/services/navigation.service';

@Component({
	selector: 'app-process-list',
	imports: [
		TranslatePipe,
		ListLayoutComponent,
		TitleGroupComponent,
		ToolbarComponent,
		TableComponent,
		AlertComponent,
		ButtonComponent,
		ModalCreateProcessComponent,
		ModalCurlRequestComponent,
		TableFilterComponent,
		SelectComponent,
	],
	templateUrl: './process-list.component.html',
	styleUrl: './process-list.component.scss',
	encapsulation: ViewEncapsulation.None,
})
export class ProcessListComponent extends PaginatedListController implements OnInit {
	trans = inject(TranslateService);
	processHelper = inject(ProcessHelperService);
	processService = inject(ProcessService);
	loading = inject(LoadingService);
	masterService = inject(MasterService);
	toolbarHelperService = inject(ToolbarHelperService);
	location = inject(Location);
	activatedRoute = inject(ActivatedRoute);
	rolePermissions = inject(RolePermissionsService);
	private snackbarService = inject(SnackbarService);
	private modalConfirmationService = inject(ModalConfirmationService);
	private destroyRef = inject(DestroyRef);
	private navigationService = inject(NavigationService);

	protected readonly Permission = Permission;

	// TABLE
	public toolbar: Signal<IToolbar>;
	public header: ITableHeader[] = this.processHelper.header;
	public override config = this.processHelper.config as ITableConfig;
	public tableFilters: IProcessListPayload;
	private sort: ITableSortEvent = this.processHelper.sort;
	public content: ITableRow[] = [];
	private lastRawData: IProcessListBody[] = [];
	public hasReviewProcess: boolean = false;
	public showModalCreateProcess: boolean = false;
	private lastCompanyUuid: string | null = null;
	readonly showKoErrorCodeFilter = signal(false);
	errorCodeOptions: ISelect[] = [];
	selectedErrorCode: ISelect | null = null;
	operationTypeOptions: ISelect[] = [];
	selectedOperationTypes: ISelect[] = [];

	constructor() {
		super();
		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			if (modalResponse?.id?.menuAction === MenuItemAction.DELETE) {
				if (modalResponse?.action === 'buttonPrimary') {
					const processUuid: string = modalResponse?.id?.processUuid;
					this.confirmDeleteProcess(processUuid);
				}
			}
		});
	}

	ngOnInit(): void {
		this.getReviewProcess();
		// NIF-1005: deep-link desde Panel (status, error_code, operation_type, fechas, company).
		const deepLinkFilters = this.buildFiltersFromQueryParams();
		if (deepLinkFilters) {
			this.tableFilters = {
				...formatFilterTable(this.config.pagination),
				...deepLinkFilters,
			} as IProcessListPayload;
		}

		let restoredFromToolbar = false;
		this.toolbarHelperService.initializeListToolbar(
			this,
			() => {
				this.processHelper.resetFilters();
				return this.processHelper.toolbar;
			},
			(saved) => {
				if (saved?.tableFilters && Object.keys(saved.tableFilters).length) {
					restoredFromToolbar = true;
					this.tableFilters = { ...saved.tableFilters };
					this.processHelper.setFiltersFromPayload(saved.tableFilters as Record<string, any>);
					this.syncKoUiFromTableFilters();
				}
				if (saved?.pagination) {
					this.hasPrevPagination = true;
					this.config.pagination = saved.pagination;
				}
				if (saved?.tableSort?.sortKey && saved?.tableSort?.sortDirection) {
					const idx = this.processHelper.header.findIndex((h) => h.sortKey === saved.tableSort.sortKey);
					this.sort = {
						sortKey: saved.tableSort.sortKey,
						sortDirection: saved.tableSort.sortDirection as ITableSortEvent['sortDirection'],
						headerIndex:
							saved.tableSort.headerIndex !== undefined && saved.tableSort.headerIndex >= 0
								? saved.tableSort.headerIndex
								: idx >= 0
									? idx
									: 0,
					};
				}
			},
		);

		// Tras resetFilters del toolbar: hidratar chips/franja KO si venimos del deep-link.
		if (deepLinkFilters && !restoredFromToolbar) {
			this.processHelper.setFiltersFromPayload(this.tableFilters as Record<string, any>);
			this.syncKoUiFromTableFilters();
		}

		// Una sola carga inicial de /profiles (+ metadata si hay compañía efectiva).
		const companyUuids = this.resolveInitialCompanyUuids();
		this.lastCompanyUuid = this.companySelectionKey(companyUuids);
		this.processHelper.loadWorkflowOptions(companyUuids.length ? companyUuids : null);
		const metadataCompany = companyUuids.length === 1 ? companyUuids[0] : null;
		this.processHelper.updateMetadataKeys(
			metadataCompany,
			metadataCompany ? this.processHelper.getFilterValuesSnapshot() : {},
		);

		this.operationTypeOptions = this.masterService.operationsTypes ?? [];
		this.syncSelectedOperationTypesFromFilters();

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.refreshErrorCodeOptions();
			this.operationTypeOptions = this.masterService.operationsTypes ?? [];
			this.header = this.processHelper.header;
			this.config = {
				...this.processHelper.config,
				pagination: {
					...this.config.pagination,
					label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				},
			};
			if (this.lastRawData.length > 0) {
				this.content = this.processHelper.transformRow(this.lastRawData);
			}
		});
	}

	protected override beforeListRequest(): void {
		delete this.config.status;
		this.loading.showTable(this.config, this.header.length);
		this.sanitizeErrorCodeBeforeRequest();
	}

	protected override afterListRequest(): void {
		this.loading.hideTable(this.config);
	}

	protected createListRequest(): Observable<IProcessListResponse> {
		this.getPayload();
		return this.processService.list(this.tableFilters);
	}

	protected applyListResponse(response: IProcessListResponse): void {
		this.processHelper.setAvailableKoErrorCodes(response.meta?.available_error_codes);
		this.refreshErrorCodeOptions();
		this.updateKoErrorCodeFilterVisibility();
		this.syncSelectedErrorCodeFromFilters();
		this.syncSelectedOperationTypesFromFilters();
		this.lastRawData = response.data;
		this.content = this.processHelper.transformRow(response.data);
	}

	protected override onListError(_error: HttpErrorResponse): void {
		this.config['status'] = { type: 'error' };
	}

	private getReviewProcess(): void {
		const uuidPendingState = this.masterService.allStatus
			.find((s) => s.value === PROCESS_STATUS.PENDING)
			?.id?.toString();
		if (!uuidPendingState) return;
		this.processService
			.list({
				state_uuid: uuidPendingState,
				page: 1,
				per_page: 5,
				sort_by: 'created_at',
				sort_order: 'desc',
			})
			.subscribe({
				next: (response: IProcessListResponse) => {
					this.hasReviewProcess = Boolean(response.data.length);
				},
				error: (error: HttpErrorResponse) => {},
				complete: () => {},
			});
	}

	private getPayload(): void {
		this.tableFilters = this.withPagination(this.tableFilters, {
			sort_by: this.sort.sortKey,
			sort_order: this.sort.sortDirection,
		}) as IProcessListPayload;
		this.toolbarHelperService.cleanSavedToolbar(this.constructor.name);
	}

	public tableCell(cell: ITableCellEmitter): void {
		const actions = {
			[MenuItemAction.DELETE]: () => {
				this.deleteProcess(cell?.rowId);
			},
		};

		const actionId = cell?.cellId?.id || cell?.cellId;

		actions[actionId] ? actions[actionId]() : null;
	}

	private deleteProcess(process_uuid?: string): void {
		if (!process_uuid) return;
		this.modalConfirmationService.show({
			title: this.trans.instant('PROCESSES.MODAL_DELETE.TITLE'),
			description: this.trans.instant('PROCESSES.MODAL_DELETE.DESCRIPTION'),
			buttonPrimaryLabel: this.trans.instant('BUTTON.DELETE'),
			buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
			buttonPrimaryStyle: true,
			id: { processUuid: process_uuid, menuAction: MenuItemAction.DELETE },
		});
	}

	private confirmDeleteProcess(processUuid?: string): void {
		if (!processUuid) return;
		this.loading.show(this.trans.instant('LOADING.DELETE_PROCESS'));
		this.processService.delete(processUuid).subscribe({
			next: () => {
				this.getList();
				this.snackbarService.show(this.trans.instant('PROCESSES.LIST.DELETE_CONFIRM'));
			},
			error: (_error) => {
				this.loading.hide();
				this.alertError = this.trans.instant('PROCESSES.LIST.DELETE_ERROR');
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public tableRow(rowClick: ITableRow & { event?: MouseEvent }): void {
		this.goToDetail(rowClick.clickValue, rowClick.event);
	}

	private goToDetail(id: any, event?: MouseEvent | null): void {
		const { sort_by, sort_order, ...tableFiltersWithoutSort } = this.tableFilters || {};
		const basePath = '/' + (this.location?.path().split('/').filter(Boolean)[0] ?? 'processes');
		this.toolbarHelperService.saveToolbarPagination(
			tableFiltersWithoutSort,
			this.toolbar,
			this.config.pagination,
			basePath,
			null,
			this.constructor.name,
			{
				sortKey: this.sort.sortKey,
				sortDirection: this.sort.sortDirection,
				headerIndex: this.sort.headerIndex,
			},
		);

		this.navigationService.navigateTo({ routeSegments: ['processes', 'detail', id] }, event);
	}

	public sortChange(event: ITableSortEvent): void {
		// Si el sortDirection es nullish, significa que se ha desactivado el ordenamiento
		if (!event.sortDirection) this.sort = this.processHelper.resetSort();
		else this.sort = event;

		this.getList();
	}

	public override filterChange(filters: any): void {
		// NIF-929: franja KO (error_code / operation_type_uuids) vive fuera del form del toolbar;
		// el emit hace replace y no trae esos campos — preservar mientras Rechazado siga activo.
		const prevErrorCode = this.tableFilters?.error_code;
		const prevSelectedErrorCode = this.selectedErrorCode;
		const prevOperationTypeUuids = (this.tableFilters as Record<string, unknown> | undefined)?.[
			'operation_type_uuids'
		];
		const prevSelectedOperationTypes = this.selectedOperationTypes;

		this.tableFilters = transformTrimObject(filters);

		if (!this.processHelper.isKoSelectedInFilter(this.tableFilters?.state_uuid)) {
			delete this.tableFilters.error_code;
			this.selectedErrorCode = null;
			this.hideKoErrorCodeFilterImmediately();
		} else {
			if (this.tableFilters.error_code == null && prevErrorCode != null) {
				this.tableFilters.error_code = prevErrorCode;
				this.selectedErrorCode = prevSelectedErrorCode;
			}
			const nextOps = (this.tableFilters as Record<string, unknown>)['operation_type_uuids'];
			// El form del toolbar puede emitir "" (control vacío) aunque la franja KO tenga valor.
			if ((nextOps == null || nextOps === '') && prevOperationTypeUuids != null && prevOperationTypeUuids !== '') {
				(this.tableFilters as Record<string, unknown>)['operation_type_uuids'] = prevOperationTypeUuids;
				this.selectedOperationTypes = prevSelectedOperationTypes;
			}
			this.updateKoErrorCodeFilterVisibility();
		}

		this.syncSelectedOperationTypesFromFilters();

		// NIF-713 / NIF-756: company_uuid (multi) fuera de «Más filtros»; sincronizar workflows/metadata.
		this.syncCompanyDependentOptions(filters);

		this.setListConfigPagination(
			this.hasPrevPagination
				? { current_page: this.config?.pagination?.page, total: this.config?.pagination?.total }
				: { current_page: 1 },
		);
		this.hasPrevPagination = false;
		this.getList();
	}

	public readonly curlModalOpen = signal(false);

	public openCurlModal(): void {
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public onToolbarAction(action: string | { id?: string }): void {
		const id = typeof action === 'string' ? action : action?.id;
		if (id === MenuItemAction.SHOW_CURL) {
			this.openCurlModal();
		}
	}

	public getProcessesListCurl(): string {
		if (!this.tableFilters) return '';
		return buildCurlPostJson(environment.apiUrl, '/processes', this.tableFilters as object);
	}

	public filterLiveChange(filters: any): void {
		// "Limpiar" en Más filtros: solo vacía el FormGroup (formFilterLive). No aplicar filtros.
		if (
			filters &&
			typeof filters === 'object' &&
			Object.keys(filters).length > 0 &&
			Object.values(filters).every((v) => v === null)
		) {
			this.lastCompanyUuid = this.singleCompanyUuid();
			// Multi-compañía: sin compañía en el borrador → vaciar opciones de metadata.
			// Una sola compañía: metadata ya cargada en init, no hace falta tocar.
			if (!this.lastCompanyUuid) {
				this.processHelper.updateMetadataKeys(null);
				this.processHelper.loadWorkflowOptions();
			}
			return;
		}

		if (filters && Object.prototype.hasOwnProperty.call(filters, 'state_uuid')) {
			if (!this.processHelper.isKoSelectedInFilter(filters.state_uuid)) {
				this.hideKoErrorCodeFilterImmediately();
			} else {
				this.updateKoErrorCodeFilterVisibility(filters.state_uuid);
			}
		}

		this.syncCompanyDependentOptions(filters);
	}

	/**
	 * Actualiza opciones de workflow/metadata cuando cambia la compañía (una o varias).
	 * Usado desde filterChange (toolbar visible) y filterLiveChange (Más filtros).
	 */
	private syncCompanyDependentOptions(filters: any): void {
		const selected = this.resolveCompanyUuidsFromFilter(filters?.company_uuid);
		const fallback = this.singleCompanyUuid();
		const effective = selected.length ? selected : fallback ? [fallback] : [];
		const key = this.companySelectionKey(effective);

		if (key === this.lastCompanyUuid) return;

		this.lastCompanyUuid = key;
		const { metadata_key, metadata_value, workflow_uuid, ...filtersWithoutMeta } = filters ?? {};
		// Metadata por compañía solo con exactamente 1 (API 1:1). Con selector multi: N>1 limpia keys.
		if (!this.singleCompanyUuid()) {
			const metadataCompany = selected.length === 1 ? selected[0] : null;
			this.processHelper.updateMetadataKeys(metadataCompany, filtersWithoutMeta);
		}
		this.processHelper.loadWorkflowOptions(selected.length ? selected : fallback);
	}

	/** Compañías del filtro restaurado, o la única del usuario si no hay selector. */
	private resolveInitialCompanyUuids(): string[] {
		const fromFilters = this.resolveCompanyUuidsFromFilter(this.tableFilters?.company_uuid);
		if (fromFilters.length) {
			return fromFilters;
		}
		const single = this.singleCompanyUuid();
		return single ? [single] : [];
	}

	private resolveCompanyUuidsFromFilter(companyFilter: unknown): string[] {
		if (companyFilter == null || companyFilter === '') {
			return [];
		}
		const arr = Array.isArray(companyFilter) ? companyFilter : [companyFilter];
		return arr
			.map((item) => {
				if (item != null && typeof item === 'object' && 'id' in item) {
					return (item as { id?: string | number }).id;
				}
				return item;
			})
			.filter((id) => id != null && String(id).trim() !== '')
			.map((id) => String(id));
	}

	private companySelectionKey(uuids: string[]): string | null {
		if (!uuids.length) {
			return null;
		}
		return [...uuids].sort().join(',');
	}

	private singleCompanyUuid(): string | null {
		if (this.rolePermissions.isSuperadmin() || (this.masterService.companies?.length ?? 0) !== 1) {
			return null;
		}
		const id = this.masterService.companies[0]?.id;
		return id != null ? String(id) : null;
	}

	public goToReviewProcess(event: Event): void {
		this.navigationService.navigateTo({ routeSegments: ['processes', 'review'] }, event);
	}

	public onErrorCodeSelect(option: ISelect | ISelect[] | null): void {
		if (Array.isArray(option)) {
			return;
		}

		this.selectedErrorCode = option;

		if (!option?.id) {
			const next = { ...this.tableFilters };
			delete next.error_code;
			this.tableFilters = next;
		} else {
			this.tableFilters = { ...this.tableFilters, error_code: Number(option.id) };
		}

		this.setListConfigPagination({ current_page: 1 });
		this.getList();
	}

	public onKoOperationTypeSelect(option: ISelect | ISelect[] | null): void {
		const selected = option == null ? [] : Array.isArray(option) ? option : [option];
		this.selectedOperationTypes = selected;

		const next = { ...this.tableFilters } as IProcessListPayload & Record<string, unknown>;
		if (!selected.length) {
			delete next.operation_type_uuids;
		} else {
			next.operation_type_uuids = selected as unknown as string[];
		}
		this.tableFilters = next as IProcessListPayload;

		const filterVals = { ...this.processHelper.getFilterValuesSnapshot() };
		if (!selected.length) {
			delete filterVals['operation_type_uuids'];
		} else {
			filterVals['operation_type_uuids'] = selected;
		}
		this.processHelper.setFilterValues(filterVals);

		this.setListConfigPagination({ current_page: 1 });
		this.getList();
	}

	private updateKoErrorCodeFilterVisibility(stateUuid?: unknown): void {
		const koSelected = this.processHelper.isKoSelectedInFilter(
			stateUuid !== undefined ? stateUuid : this.tableFilters?.state_uuid,
		);

		// NIF-975: franja KO (ops + motivos) visible con Rechazado para cualquier rol del listado.
		// Motivos se ocultan en plantilla si no hay códigos.
		if (!koSelected) {
			this.hideKoErrorCodeFilterImmediately();
			return;
		}

		this.showKoErrorCodeFilter.set(true);
	}

	private hideKoErrorCodeFilterImmediately(): void {
		this.showKoErrorCodeFilter.set(false);
		this.selectedErrorCode = null;
		this.errorCodeOptions = [];
		this.processHelper.setAvailableKoErrorCodes([]);
	}

	private refreshErrorCodeOptions(): void {
		this.errorCodeOptions = this.processHelper.getProcessErrorCodeOptions();
	}

	private syncSelectedErrorCodeFromFilters(): void {
		const raw = this.tableFilters?.error_code;
		if (raw == null) {
			this.selectedErrorCode = null;
			return;
		}

		const code = typeof raw === 'object' ? Number((raw as ISelect).id) : Number(raw);
		this.selectedErrorCode = this.errorCodeOptions.find((o) => Number(o.id) === code) ?? null;
	}

	private syncSelectedOperationTypesFromFilters(): void {
		const raw = (this.tableFilters as Record<string, unknown> | undefined)?.['operation_type_uuids'];
		if (raw == null) {
			this.selectedOperationTypes = [];
			return;
		}
		const arr = Array.isArray(raw) ? raw : [raw];
		this.selectedOperationTypes = arr
			.map((v) => {
				if (v != null && typeof v === 'object' && 'id' in (v as object)) {
					return v as ISelect;
				}
				return this.operationTypeOptions.find((o) => String(o.id) === String(v));
			})
			.filter((o): o is ISelect => !!o);
	}

	private sanitizeErrorCodeBeforeRequest(): void {
		if (this.tableFilters?.error_code == null) {
			return;
		}

		// NIF-975: error_code válido para cualquier rol; solo exigir filtro Rechazado activo.
		if (!this.processHelper.isKoSelectedInFilter(this.tableFilters.state_uuid)) {
			delete this.tableFilters.error_code;
			this.selectedErrorCode = null;
			return;
		}

		// Validación solo cuando ya tenemos opciones (evita borrar error_code en el primer getList
		// antes de recibir meta.available_error_codes).
		if (this.errorCodeOptions.length > 0) {
			const code = Number(this.tableFilters.error_code);
			const valid = this.errorCodeOptions.some((o) => Number(o.id) === code);
			if (!valid) {
				delete this.tableFilters.error_code;
				this.selectedErrorCode = null;
			}
		}
	}

	/** NIF-1005: construye filtros API/toolbar desde query params del deep-link. */
	private buildFiltersFromQueryParams(): Record<string, unknown> | null {
		const q = this.activatedRoute.snapshot.queryParamMap;
		const payload: Record<string, unknown> = {};
		let hasAny = false;

		const status = q.get('status');
		if (status) {
			const statusOption = this.masterService.allStatus.find((s) => s.value === status);
			if (statusOption) {
				payload['state_uuid'] = statusOption.id;
				hasAny = true;
			}
		}

		const errorCodeRaw = q.get('error_code');
		if (errorCodeRaw != null && errorCodeRaw !== '') {
			const code = Number(errorCodeRaw);
			if (Number.isFinite(code) && code > 0) {
				payload['error_code'] = code;
				hasAny = true;
			}
		}

		const operationTypeUuid = q.get('operation_type_uuids')?.trim();
		if (operationTypeUuid) {
			// API exige array; el query param llega como string único.
			payload['operation_type_uuids'] = [operationTypeUuid];
			hasAny = true;
		}

		const startAt = q.get('start_at');
		if (startAt) {
			payload['start_at'] = startAt;
			hasAny = true;
		}
		const endAt = q.get('end_at');
		if (endAt) {
			payload['end_at'] = endAt;
			hasAny = true;
		}

		const companyUuid = q.get('company_uuid')?.trim();
		if (companyUuid) {
			payload['company_uuid'] = companyUuid;
			hasAny = true;
		}

		return hasAny ? payload : null;
	}

	/** Sincroniza franja KO + selects con `tableFilters` (deep-link o restore). */
	private syncKoUiFromTableFilters(): void {
		this.updateKoErrorCodeFilterVisibility();
		const raw = this.tableFilters?.error_code;
		if (raw != null) {
			const code = typeof raw === 'object' ? Number((raw as ISelect).id) : Number(raw);
			if (Number.isFinite(code) && code > 0) {
				this.selectedErrorCode =
					this.errorCodeOptions.find((o) => Number(o.id) === code) ??
					({ id: code, label: String(code) } as ISelect);
			}
		} else {
			this.selectedErrorCode = null;
		}
		this.syncSelectedOperationTypesFromFilters();
	}
}
